"use strict";
/**
 * Paid off platform — ops marks an invoice paid without platform money movement.
 *
 * Prefer these helpers over ad-hoc `status === 'paid'` checks:
 *
 * | Use case | Helper |
 * | --- | --- |
 * | Platform GTV / digest / revenue (real Handshake money) | `isPlatformCollectedInvoice` / `sqlInvoiceCountsTowardPlatformGtv` |
 * | Project settlement UX (list collected / released / to-be-collected) | `sqlInvoiceCountsTowardProjectSettlement` (= paid, **includes** off-platform) |
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAID_OFF_PLATFORM_LABEL = exports.PAID_OFF_PLATFORM_BY_METADATA_KEY = exports.PAID_OFF_PLATFORM_AT_METADATA_KEY = exports.PAID_OFF_PLATFORM_METADATA_KEY = void 0;
exports.isPaidOffPlatform = isPaidOffPlatform;
exports.isPlatformCollectedInvoice = isPlatformCollectedInvoice;
exports.platformCollectedAmount = platformCollectedAmount;
exports.sqlInvoiceCountsTowardPlatformGtv = sqlInvoiceCountsTowardPlatformGtv;
exports.sqlInvoiceCountsTowardProjectSettlement = sqlInvoiceCountsTowardProjectSettlement;
exports.sqlInvoiceIsPaidOffPlatform = sqlInvoiceIsPaidOffPlatform;
exports.PAID_OFF_PLATFORM_METADATA_KEY = "paid_off_platform";
exports.PAID_OFF_PLATFORM_AT_METADATA_KEY = "paid_off_platform_at";
exports.PAID_OFF_PLATFORM_BY_METADATA_KEY = "paid_off_platform_by";
/** User-facing / ops payment status label. */
exports.PAID_OFF_PLATFORM_LABEL = "Paid off platform";
function metadataFlagTrue(metadata, key) {
    if (!metadata || typeof metadata !== "object")
        return false;
    const value = metadata[key];
    return value === true || value === "true";
}
/** True when ops marked this invoice paid outside Handshake. */
function isPaidOffPlatform(invoice) {
    if (!invoice)
        return false;
    return metadataFlagTrue(invoice.paymentMetadata, exports.PAID_OFF_PLATFORM_METADATA_KEY);
}
/**
 * True when the invoice was collected through Handshake (counts toward platform GTV).
 * Off-platform paid invoices are excluded even though `status === 'paid'`.
 */
function isPlatformCollectedInvoice(invoice) {
    if (!invoice)
        return false;
    if (String(invoice.status ?? "").toLowerCase() !== "paid")
        return false;
    return !isPaidOffPlatform(invoice);
}
/** Amount to include in platform funded/collected GTV (0 for off-platform). */
function platformCollectedAmount(invoice) {
    if (!isPlatformCollectedInvoice(invoice))
        return 0;
    return Number(invoice?.milestoneAmountCaptured ?? 0) || 0;
}
/**
 * SQL boolean: invoice counts toward platform funded/released/fee GTV.
 * Excludes paid-off-platform. Use for revenue / GTV aggregates only.
 *
 * @example
 * WHERE ${sqlInvoiceCountsTowardPlatformGtv("pi")}
 * FILTER (WHERE ${sqlInvoiceCountsTowardPlatformGtv("pi")})
 */
function sqlInvoiceCountsTowardPlatformGtv(alias = "pi") {
    return `${alias}.status = 'paid' AND COALESCE(${alias}.payment_metadata->>'${exports.PAID_OFF_PLATFORM_METADATA_KEY}', 'false') <> 'true'`;
}
/**
 * SQL boolean: invoice counts toward project settlement (money-identity collected/released).
 * Includes paid-off-platform so project lists treat ops-settled invoices as funded, not outstanding.
 */
function sqlInvoiceCountsTowardProjectSettlement(alias = "pi") {
    return `${alias}.status = 'paid'`;
}
/** SQL boolean: invoice was marked paid off platform. */
function sqlInvoiceIsPaidOffPlatform(alias = "pi") {
    return `COALESCE(${alias}.payment_metadata->>'${exports.PAID_OFF_PLATFORM_METADATA_KEY}', 'false') = 'true'`;
}
